import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
  Popover,
  TextField,
  FormControl,
  Select,
  MenuItem,
  ListItemText
} from '@mui/material';
import {
  AccountTree,
  KeyboardArrowDown,
  Edit,
  Delete,
  Add,
  LocalOffer,
  Flag,
  Clear,
  Check
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSubtasks, updateTicketPartial } from '../../store/ticketSlice';
import { COLORS } from '../../shared/colors';
import { ENDPOINTS } from '../../services/endpoints';
import TaskCompletionCheckbox from './TaskCompletionCheckbox';
import AssigneeSelector from '../../shared/AssigneeSelector';
import OverflowTooltip from '../../shared/OverflowTooltip';
import DropdownSelector from './DropdownSelector';
import AssigneeAvatarStack from '../../shared/AssigneeAvatarStack';
import { taskSelectMenuProps } from '../../shared/globalSX';
import { getPriorityColor, getPriorityName } from '../../shared/commonFunctions';
import { getStatusIcon } from '../../shared/iconUtils';
import { toast } from 'react-toastify';

const NestedSubtaskDrawer = ({ 
  subtask, 
  level = 0, 
  onEdit, 
  onDelete, 
  onCompletionChange, 
  onAddSubtask,
  onLabelsUpdate,
  onSubtaskUpdate,
  fullScreen = false,
  readOnly = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dropdowns } = useSelector((state) => state.tickets);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [nestedSubtasks, setNestedSubtasks] = useState([]);
  const [localCompleted, setLocalCompleted] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [localSubtask, setLocalSubtask] = useState(subtask);
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [isSavingInlineEdit, setIsSavingInlineEdit] = useState(false);
  const [editDraft, setEditDraft] = useState({
    title: subtask.title || '',
    statusId: subtask.status_id || subtask.status?.id || subtask.status || '',
    assigneeIds: Array.isArray(subtask.assignees) && subtask.assignees.length > 0
      ? subtask.assignees.map((assignee) => (typeof assignee === 'object' ? assignee.id : assignee)).filter(Boolean)
      : (subtask.assignee ? [typeof subtask.assignee === 'object' ? subtask.assignee.id : subtask.assignee] : []),
    priorityId: subtask.priority_id || subtask.priority?.id || subtask.priority || '',
    dueDate: subtask.due_date || subtask.end_date || subtask.dueDate || ''
  });
  
  // Label Popover State
  const [labelAnchorEl, setLabelAnchorEl] = useState(null);
  const [localLabels, setLocalLabels] = useState(subtask.labels || []);

  const resolveDropdownId = useCallback((value, items = []) => {
    if (!value) return '';
    if (typeof value === 'object') return value.id || '';

    const directMatch = items.find((item) => String(item.id) === String(value));
    if (directMatch) return directMatch.id;

    const normalizedValue = String(value).toLowerCase();
    const nameMatch = items.find((item) => String(item.name || '').toLowerCase() === normalizedValue);
    if (nameMatch) return nameMatch.id;

    return value;
  }, []);

  // Update local labels when subtask labels change from prop
  React.useEffect(() => {
    if (subtask.labels) {
      setLocalLabels(subtask.labels);
    }
  }, [subtask.labels]);

  React.useEffect(() => {
    setLocalSubtask(subtask);

    if (!isInlineEditing) {
      const currentAssignees = Array.isArray(subtask.assignees) && subtask.assignees.length > 0
        ? subtask.assignees
        : (subtask.assignee ? [subtask.assignee] : []);

      setEditDraft({
        title: subtask.title || '',
        statusId: resolveDropdownId(subtask.status_id || subtask.status?.id || subtask.status_name || subtask.status || '', dropdowns.statuses || []),
        assigneeIds: currentAssignees.map((assignee) => (typeof assignee === 'object' ? assignee.id : assignee)).filter(Boolean),
        priorityId: resolveDropdownId(subtask.priority_id || subtask.priority?.id || subtask.priority_name || subtask.priority || '', dropdowns.priorities || []),
        dueDate: normalizeDateValue(subtask.due_date || subtask.end_date || subtask.dueDate || '')
      });
    }
  }, [dropdowns.priorities, dropdowns.statuses, isInlineEditing, resolveDropdownId, subtask]);

  const getIdValue = useCallback((value) => {
    if (value && typeof value === 'object') return value.id;
    return value || '';
  }, []);

  const getSelectedAssigneeObjects = useCallback((ids = []) => (
    (ids || [])
      .map((id) => dropdowns.assignees?.find((assignee) => String(assignee.id) === String(id)))
      .filter(Boolean)
  ), [dropdowns.assignees]);

  const getAssigneeIds = useCallback((value) => {
    const list = Array.isArray(value) ? value : (value ? [value] : []);
    return list.map((item) => getIdValue(item)).filter(Boolean);
  }, [getIdValue]);

  const getPriorityOption = useCallback((priorityId) => {
    if (!priorityId) return null;
    return dropdowns.priorities?.find((priority) => String(priority.id) === String(priorityId)) || null;
  }, [dropdowns.priorities]);

  const getStatusOption = useCallback((statusId) => {
    if (!statusId) return null;
    return dropdowns.statuses?.find((status) => String(status.id) === String(statusId)) || null;
  }, [dropdowns.statuses]);

  const startInlineEdit = useCallback((event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setIsInlineEditing(true);
    setEditDraft({
      title: localSubtask?.title || subtask.title || '',
      statusId: resolveDropdownId(localSubtask?.status_id || localSubtask?.status || localSubtask?.status_name || subtask.status_id || subtask.status || subtask.status_name || '', dropdowns.statuses || []),
      assigneeIds: getAssigneeIds(localSubtask?.assignees || localSubtask?.assignee || subtask.assignees || subtask.assignee || []),
      priorityId: resolveDropdownId(localSubtask?.priority_id || localSubtask?.priority || localSubtask?.priority_name || subtask.priority_id || subtask.priority || subtask.priority_name || '', dropdowns.priorities || []),
      dueDate: normalizeDateValue(localSubtask?.due_date || localSubtask?.end_date || localSubtask?.dueDate || subtask.due_date || subtask.end_date || subtask.dueDate || '')
    });
  }, [dropdowns.priorities, dropdowns.statuses, getAssigneeIds, localSubtask, resolveDropdownId, subtask]);

  const cancelInlineEdit = useCallback((event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setIsInlineEditing(false);
    setEditDraft({
      title: subtask.title || '',
      statusId: resolveDropdownId(subtask.status_id || subtask.status?.id || subtask.status_name || subtask.status || '', dropdowns.statuses || []),
      assigneeIds: Array.isArray(subtask.assignees) && subtask.assignees.length > 0
        ? subtask.assignees.map((assignee) => (typeof assignee === 'object' ? assignee.id : assignee)).filter(Boolean)
        : (subtask.assignee ? [typeof subtask.assignee === 'object' ? subtask.assignee.id : subtask.assignee] : []),
      priorityId: resolveDropdownId(subtask.priority_id || subtask.priority?.id || subtask.priority_name || subtask.priority || '', dropdowns.priorities || []),
      dueDate: normalizeDateValue(subtask.due_date || subtask.end_date || subtask.dueDate || '')
    });
  }, [dropdowns.priorities, dropdowns.statuses, resolveDropdownId, subtask]);

  const patchLocalTask = useCallback((updates) => {
    setLocalSubtask((prev) => ({ ...prev, ...updates }));
    if (onSubtaskUpdate) {
      onSubtaskUpdate(subtask.id, updates);
    }
  }, [onSubtaskUpdate, subtask.id]);

  const handleSaveInlineEdit = useCallback(async (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (isSavingInlineEdit || !subtask?.id) return;

    const nextTitle = editDraft.title.trim();
    const nextStatusId = editDraft.statusId || null;
    const nextAssigneeIds = getAssigneeIds(editDraft.assigneeIds);
    const nextPriorityId = editDraft.priorityId || null;
    const nextDueDate = normalizeDateValue(editDraft.dueDate);

    const payload = {};

    const currentStatusValue = resolveDropdownId(subtask.status_id || subtask.status?.id || subtask.status_name || subtask.status || '', dropdowns.statuses || []);
    const currentPriorityValue = resolveDropdownId(subtask.priority_id || subtask.priority?.id || subtask.priority_name || subtask.priority || '', dropdowns.priorities || []);

    if (nextTitle !== (subtask.title || '')) payload.title = nextTitle;
    if (String(nextStatusId || '') !== String(currentStatusValue || '')) {
      payload.status_id = nextStatusId;
    }
    if (JSON.stringify(nextAssigneeIds) !== JSON.stringify(getAssigneeIds(subtask.assignees || subtask.assignee || []))) {
      payload.assignee = nextAssigneeIds;
    }
    if (String(nextPriorityId || '') !== String(currentPriorityValue || '')) {
      payload.priority_id = nextPriorityId;
    }
    if (nextDueDate !== normalizeDateValue(subtask.due_date || subtask.end_date || subtask.dueDate || '')) {
      payload.due_date = nextDueDate || null;
    }

    if (Object.keys(payload).length === 0) {
      setIsInlineEditing(false);
      return;
    }

    setIsSavingInlineEdit(true);

    try {
      await dispatch(updateTicketPartial({
        ticketId: subtask.id,
        ticketData: payload
      })).unwrap();

      const resolvedStatus = getStatusOption(nextStatusId);
      const resolvedPriority = getPriorityOption(nextPriorityId);
      const resolvedAssignees = getSelectedAssigneeObjects(nextAssigneeIds);

      const optimisticUpdates = {
        title: nextTitle,
        status_id: nextStatusId,
        status: resolvedStatus?.name || nextStatusId || null,
        status_name: resolvedStatus?.name || nextStatusId || null,
        assignees: resolvedAssignees,
        assignee: resolvedAssignees[0] || null,
        priority_id: nextPriorityId,
        priority: resolvedPriority || nextPriorityId || null,
        priority_name: resolvedPriority?.name || nextPriorityId || null,
        priority_color: resolvedPriority?.colour || resolvedPriority?.color || subtask.priority_color || null,
        due_date: nextDueDate || null,
        end_date: nextDueDate || null,
        dueDate: nextDueDate || null
      };

      setLocalSubtask((prev) => ({ ...prev, ...optimisticUpdates }));
      patchLocalTask(optimisticUpdates);
      setIsInlineEditing(false);
    } catch (error) {
      console.error('Failed to update subtask:', error);
      toast.error(t('taskViewModal.saveError', 'Failed to save changes'));
    } finally {
      setIsSavingInlineEdit(false);
    }
  }, [dispatch, dropdowns.priorities, dropdowns.statuses, editDraft.assigneeIds, editDraft.dueDate, editDraft.priorityId, editDraft.statusId, editDraft.title, getAssigneeIds, getPriorityOption, getSelectedAssigneeObjects, getStatusOption, isSavingInlineEdit, patchLocalTask, resolveDropdownId, subtask, t]);

  const handleInlineFieldChange = (field) => (event) => {
    const value = event?.target?.value;
    setEditDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubtaskCompletionChange = async (taskId, completed) => {
    try {
      if (taskId === subtask.id) {
        // Optimistically update local completion status
        setLocalCompleted(completed);
        // Notify parent component
        if (onCompletionChange) {
          await onCompletionChange(taskId, completed);
        }
      } else {
        // Optimistically update nested subtasks
        setNestedSubtasks(prev => prev.map(task => 
          task.id === taskId ? { ...task, marked_as_completed: completed } : task
        ));
      }
    } catch (error) {
      console.error('Subtask completion update failed:', error);
      // Revert optimistic updates
      if (taskId === subtask.id) {
        setLocalCompleted(!completed);
      } else {
        setNestedSubtasks(prev => prev.map(task => 
          task.id === taskId ? { ...task, marked_as_completed: !completed } : task
        ));
      }
    }
  };

  const getStatusColor = (statusName) => {
    const statusLower = statusName?.toLowerCase() || '';
    if (statusLower.includes('done') || statusLower.includes('closed')) return COLORS.status.done;
    if (statusLower.includes('progress')) return COLORS.status.inProgress;
    if (statusLower.includes('review')) return COLORS.status.review;
    if (statusLower.includes('blocked')) return COLORS.status.blocked;
    return COLORS.status.open;
  };

  const normalizeDateValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value.slice(0, 10);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  };

  const handleToggleNested = useCallback(async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!isExpanded && (subtask.subtask_count > 0 || nestedSubtasks.length > 0)) {
      if (nestedSubtasks.length === 0) {
        setIsLoading(true);
        try {
          const result = await dispatch(fetchSubtasks(subtask.id)).unwrap();
          setNestedSubtasks(result);
        } catch (error) {
          console.error('Failed to fetch nested subtasks:', error);
        } finally {
          setIsLoading(false);
        }
      }
    }
    setIsExpanded(!isExpanded);
  }, [dispatch, subtask.id, subtask.subtask_count, isExpanded, nestedSubtasks.length]);

  const handleOpenLabels = (event) => {
    event.stopPropagation();
    setLabelAnchorEl(event.currentTarget);
  };

  const handleCloseLabels = () => {
    setLabelAnchorEl(null);
  };

  const handleChildLabelsUpdate = async (taskId, newLabels) => {
    // 1. Pass up to grandparent (e.g. TaskViewModal or high-level drawer)
    if (onLabelsUpdate) {
      await onLabelsUpdate(taskId, newLabels);
    }
    // 2. Update local children list found in this drawer
    setNestedSubtasks(prev => prev.map(st => 
      st.id === taskId ? { ...st, labels: newLabels } : st
    ));
  };

  const isLabelOpen = Boolean(labelAnchorEl);
  const activeSubtask = localSubtask || subtask;
  const currentStatusId = isInlineEditing
    ? editDraft.statusId
    : resolveDropdownId(activeSubtask.status_id || activeSubtask.status?.id || activeSubtask.status_name || activeSubtask.status || '', dropdowns.statuses || []);
  const currentPriorityId = isInlineEditing
    ? editDraft.priorityId
    : resolveDropdownId(activeSubtask.priority_id || activeSubtask.priority?.id || activeSubtask.priority_name || activeSubtask.priority || '', dropdowns.priorities || []);
  const currentAssigneeIds = isInlineEditing
    ? getAssigneeIds(editDraft.assigneeIds)
    : getAssigneeIds(activeSubtask.assignees || activeSubtask.assignee || []);
  const displayStatusOption = getStatusOption(currentStatusId);
  const displayPriorityOption = getPriorityOption(currentPriorityId);
  const displayAssignees = Array.isArray(activeSubtask.assignees) && activeSubtask.assignees.length > 0
    ? activeSubtask.assignees
    : (activeSubtask.assignee ? [activeSubtask.assignee] : []);
  const displayDueDate = isInlineEditing ? editDraft.dueDate : normalizeDateValue(activeSubtask.due_date || activeSubtask.end_date || activeSubtask.dueDate || '');
  const displayTitle = isInlineEditing ? editDraft.title : (activeSubtask.title || '');
  const rowBg = COLORS.subtask.nestLevel0Bg;
  const rowHoverBg = COLORS.subtask.nestLevel0HoverBg;
  const nameIndent = level * 2;

  return (
    <Box>
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          display: 'grid',
          gridTemplateColumns: [
            'minmax(0, 1fr)',
            (fullScreen || window.location.pathname.startsWith('/task/')) ? '120px' : '100px',
            '80px',
            '60px',
            '126px',
            '104px'
          ].join(' '),
          alignItems: 'center',
          gap: (fullScreen || window.location.pathname.startsWith('/task/')) ? 3 : 0,
          px: 1, 
          py: 0.5, 
          borderBottom: `1px solid ${COLORS.ui.border}`,
          bgcolor: (isHovered || isLabelOpen) ? rowHoverBg : rowBg,
          transition: 'background-color 0.2s',
          cursor: isInlineEditing ? 'default' : 'pointer'
        }}
        onClick={() => {
          if (isInlineEditing) return;
          window.open(`/${ENDPOINTS.TASK_URL(subtask.ticket_number)}`, '_blank');
        }}
      >
        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, pl: nameIndent }}>
          {subtask.subtask_count > 0 || nestedSubtasks.length > 0 ? (
            <IconButton
              size="small"
              sx={{ p: 0.25, flexShrink: 0 }}
              onClick={(e) => { e.stopPropagation(); handleToggleNested(e); }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={14} sx={{ color: COLORS.gray[400] }} />
              ) : (
                <KeyboardArrowDown sx={{
                  fontSize: 16,
                  color: COLORS.gray[500],
                  transition: 'transform 0.2s',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                }} />
              )}
            </IconButton>
          ) : (
            <Box sx={{ width: 20, flexShrink: 0 }} />
          )}
          <TaskCompletionCheckbox 
            task={{
              id: subtask.id,
              completed: localCompleted !== null ? localCompleted : (subtask.marked_as_completed || subtask.completed || subtask.is_completed || false)
            }} 
            size={(fullScreen || window.location.pathname.startsWith('/task/')) ? 'medium' : 'small'}
            onStatusChange={handleSubtaskCompletionChange}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isInlineEditing ? (
              <TextField
                size="small"
                value={editDraft.title}
                onChange={handleInlineFieldChange('title')}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                fullWidth
                autoFocus
                inputProps={{ maxLength: 255 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 34,
                    bgcolor: COLORS.white,
                    fontSize: (fullScreen || window.location.pathname.startsWith('/task/')) ? '1rem' : '0.875rem'
                  }
                }}
              />
            ) : (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: (localCompleted || displayStatusOption?.name === 'Done' || displayStatusOption?.name === 'Closed') ? COLORS.gray[500] : COLORS.gray[800], 
                  textDecoration: (localCompleted || displayStatusOption?.name === 'Done' || displayStatusOption?.name === 'Closed') ? 'line-through' : 'none',
                  fontWeight: 500,
                  fontSize: (fullScreen || window.location.pathname.startsWith('/task/')) ? '1rem' : '0.875rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {displayTitle}
              </Typography>
            )}
          </Box>

          {(subtask.subtask_count > 0 || nestedSubtasks.length > 0) && (
            <Chip
              icon={<AccountTree sx={{ fontSize: '10px !important' }} />}
              label={nestedSubtasks.length > 0 ? nestedSubtasks.length : subtask.subtask_count}
              size="small"
              sx={{
                height: 18,
                fontSize: '10px',
                bgcolor: COLORS.gray[100],
                color: COLORS.gray[600],
                flexShrink: 0,
                '& .MuiChip-label': { px: 0.75 },
                '& .MuiChip-icon': { ml: 0.5 }
              }}
            />
          )}
        </Box>

        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {isInlineEditing ? (
            <FormControl size="small" fullWidth sx={{ maxWidth: '100%' }}>
              <Select
                value={currentStatusId || ''}
                onChange={handleInlineFieldChange('statusId')}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                displayEmpty
                sx={{
                  height: 32,
                  bgcolor: COLORS.white,
                  fontSize: '13px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[200], borderRadius: '4px' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: '0 !important',
                    height: '100%',
                    pr: '28px !important',
                    minWidth: 0,
                    overflow: 'hidden'
                  }
                }}
                renderValue={(selected) => {
                  const status = dropdowns.statuses?.find((item) => String(item.id) === String(selected)) || displayStatusOption;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', maxWidth: '100%', minWidth: 0, width: '100%' }}>
                      {getStatusIcon(status?.name) && (
                        <img src={getStatusIcon(status.name)} alt="" style={{ width: 14, height: 14, flexShrink: 0 }} />
                      )}
                      <OverflowTooltip
                        title={status?.name || t('taskViewModal.notSet')}
                        tooltipWidth={300}
                        placement="top-start"
                        sx={{ fontWeight: 500, fontSize: '13px', color: COLORS.gray[700], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                      />
                    </Box>
                  );
                }}
                MenuProps={taskSelectMenuProps}
              >
                {dropdowns.statuses?.map((status) => (
                  <MenuItem key={status.id} value={status.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    {getStatusIcon(status.name) && (
                      <img src={getStatusIcon(status.name)} alt="" style={{ width: 14, height: 14, flexShrink: 0 }} />
                    )}
                    <OverflowTooltip
                      title={status.name}
                      tooltipWidth={300}
                      placement="top-start"
                      sx={{ fontWeight: 500, fontSize: '13px', color: COLORS.gray[700], minWidth: 0, flex: 1 }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Tooltip title={displayStatusOption?.name || displayTitle || ''} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: (fullScreen || window.location.pathname.startsWith('/task/')) ? '0.8rem' : '0.75rem', 
                  color: getStatusColor(displayStatusOption?.name || activeSubtask.status || ''), 
                  bgcolor: getStatusColor(displayStatusOption?.name || activeSubtask.status || '') + '15', 
                  px: 1, 
                  py: 0.25, 
                  borderRadius: 1,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  width: '100%',
                  maxWidth: '100%',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayStatusOption?.name || activeSubtask.status || t('common.notAvailable')}
              </Typography>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {isInlineEditing ? (
            <Box sx={{ minHeight: 32, display: 'flex', alignItems: 'center', width: 'fit-content', justifyContent: 'center' }}>
              <AssigneeSelector
                value={currentAssigneeIds}
                onChange={(ids) => setEditDraft((prev) => ({ ...prev, assigneeIds: ids }))}
                assignees={dropdowns.assignees || []}
                variant="stack"
              />
            </Box>
          ) : (
            <AssigneeAvatarStack
              assignees={displayAssignees}
              size={(fullScreen || window.location.pathname.startsWith('/task/')) ? 26 : 22}
              max={3}
            />
          )}
        </Box>

        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {isInlineEditing ? (
            <FormControl size="small" fullWidth sx={{ maxWidth: '100%' }}>
              <Select
                value={currentPriorityId || ''}
                onChange={handleInlineFieldChange('priorityId')}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                displayEmpty
                renderValue={(value) => {
                  const selected = getPriorityOption(value);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                      <Tooltip title={selected?.name || ''}>
                        <Flag sx={{ fontSize: 14, color: getPriorityColor(selected || activeSubtask.priority, { priorityColor: selected?.colour || selected?.color || activeSubtask.priority_color, priorities: dropdowns.priorities }) }} />
                      </Tooltip>
                    </Box>
                  );
                }}
                sx={{
                  height: 32,
                  bgcolor: COLORS.white,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[200], borderRadius: '4px' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: '0 !important',
                    height: '100%',
                    pr: '28px !important',
                    minWidth: 0,
                    overflow: 'hidden'
                  }
                }}
                MenuProps={taskSelectMenuProps}
              >
                {dropdowns.priorities?.map((priority) => (
                  <MenuItem key={priority.id} value={priority.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Flag sx={{ fontSize: 14, color: getPriorityColor(priority, { priorityColor: priority.colour || priority.color, priorities: dropdowns.priorities }), flexShrink: 0 }} />
                    <ListItemText
                      primary={priority.name}
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: 500,
                          fontSize: '13px',
                          color: COLORS.gray[700],
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Tooltip title={getPriorityName(displayPriorityOption || activeSubtask.priority, dropdowns.priorities)}>
              <Flag
                sx={{
                  fontSize: (fullScreen || window.location.pathname.startsWith('/task/')) ? 16 : 14,
                  color: getPriorityColor(displayPriorityOption || activeSubtask.priority, {
                    priorityColor: displayPriorityOption?.colour || displayPriorityOption?.color || activeSubtask.priority_color,
                    priorities: dropdowns.priorities
                  })
                }}
              />
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{ width: '100%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {isInlineEditing ? (
            <TextField
              size="small"
              type="date"
              value={displayDueDate}
              onChange={handleInlineFieldChange('dueDate')}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                'aria-label': t('taskModal.dueDateShort', 'Due Date')
              }}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  bgcolor: COLORS.white,
                  fontSize: '13px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[200], borderRadius: '4px' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] }
                }
              }}
            />
          ) : (
            <Typography
              variant="caption"
              sx={{
                fontSize: (fullScreen || window.location.pathname.startsWith('/task/')) ? '0.8rem' : '0.72rem',
                color: displayDueDate ? COLORS.gray[700] : COLORS.gray[500],
                px: 0.5,
                py: 0.25,
                borderRadius: 1,
                fontWeight: 500,
                bgcolor: displayDueDate ? COLORS.gray[50] : 'transparent',
                width: '100%',
                maxWidth: '100%',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {displayDueDate || t('taskViewModal.noDueDate', 'No due date')}
            </Typography>
          )}
        </Box>

        <Box sx={{ width: 104, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.25 }}>
          {isInlineEditing ? (
            <>
              <Tooltip title={t('common.save', 'Save')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                <IconButton
                  size="small"
                  onClick={handleSaveInlineEdit}
                  disabled={isSavingInlineEdit}
                  sx={{
                    bgcolor: COLORS.primary,
                    color: COLORS.white,
                    width: 30,
                    height: 30,
                    '&:hover': { bgcolor: COLORS.primaryHover }
                  }}
                >
                  {isSavingInlineEdit ? <CircularProgress size={14} sx={{ color: COLORS.white }} /> : <Check sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common.cancel', 'Cancel')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                <IconButton
                  size="small"
                  onClick={cancelInlineEdit}
                  disabled={isSavingInlineEdit}
                  sx={{
                    bgcolor: COLORS.white,
                    color: COLORS.primary,
                    border: `1px solid ${COLORS.primary}`,
                    width: 30,
                    height: 30,
                    '&:hover': { bgcolor: COLORS.primary + '10' }
                  }}
                >
                  <Clear sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              {!readOnly && onAddSubtask && (
                <Tooltip title={t('taskViewModal.addSubtask', 'Add Subtask')}>
                  <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { 
                    e.stopPropagation(); 
                    if (!isExpanded && subtask.subtask_count > 0) handleToggleNested();
                    onAddSubtask(subtask.id);
                  }}>
                    <Add sx={{ fontSize: (fullScreen || window.location.pathname.startsWith('/task/')) ? 16 : 14, color: COLORS.gray[500] }} />
                  </IconButton>
                </Tooltip>
              )}
              {!readOnly && onLabelsUpdate && (
                <Tooltip title={t('common.labels', 'Labels')}>
                  <IconButton size="small" sx={{ p: 0.25 }} onClick={handleOpenLabels}>
                    <LocalOffer sx={{ fontSize: (fullScreen || window.location.pathname.startsWith('/task/')) ? 16 : 14, color: (localLabels?.length > 0) ? COLORS.primary : COLORS.gray[500] }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('common.edit', 'Edit')}>
                <IconButton
                  size="small"
                  sx={{ p: 0.25 }}
                  onClick={startInlineEdit}
                  disabled={isSavingInlineEdit}
                >
                  <Edit sx={{ fontSize: (fullScreen || window.location.pathname.startsWith('/task/')) ? 16 : 14, color: COLORS.gray[500] }} />
                </IconButton>
              </Tooltip>
              {!readOnly && onDelete && (
                <Tooltip title={t('common.delete', 'Delete')}>
                  <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { 
                    e.stopPropagation(); 
                    onDelete(subtask);
                  }}>
                    <Delete sx={{ fontSize: (fullScreen || window.location.pathname.startsWith('/task/')) ? 16 : 14, color: COLORS.error }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>

        <Popover
          open={isLabelOpen}
          anchorEl={labelAnchorEl}
          onClose={handleCloseLabels}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: { p: 1, minWidth: 200 }
          }}
        >
          <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600, px: 0.5 }}>Labels</Typography>
          <DropdownSelector
            value={localLabels?.map(l => typeof l === 'object' ? l.id : l) || []}
            onChange={async (newLabels) => {
              // Optimistically update local view
              setLocalLabels(newLabels.map(id => {
                const found = dropdowns.labels?.find(l => l.id === id);
                return found || id;
              }));
              if (onLabelsUpdate) {
                await onLabelsUpdate(subtask.id, newLabels);
              }
            }}
            items={dropdowns.labels || []}
            projectId={subtask.project_id || subtask.project?.id}
            userRole="ADMIN"
            category="labels"
            multiple={true}
            withColor={true}
            variant="minimal"
          />
        </Popover>
      </Box>

      {(subtask.subtask_count > 0 || nestedSubtasks.length > 0) && (
        <Collapse in={isExpanded} timeout={300}>
          <Box>
            {isLoading ? (
              [1, 2].map(i => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderBottom: `1px solid ${COLORS.ui.border}` }}>
                  <Box sx={{ width: 24, height: 16, borderRadius: 1, bgcolor: COLORS.gray[100] }} />
                  <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: COLORS.gray[100] }} />
                  <Box sx={{ flex: 1, height: 12, borderRadius: 1, bgcolor: COLORS.gray[100] }} />
                  <Box sx={{ width: 60, height: 20, borderRadius: 1, bgcolor: COLORS.gray[100] }} />
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: COLORS.gray[100] }} />
                  <Box sx={{ width: 14, height: 14, borderRadius: 1, bgcolor: COLORS.gray[100] }} />
                  <Box sx={{ width: 60, height: 12, borderRadius: 1, bgcolor: COLORS.gray[100] }} />
                </Box>
              ))
            ) : (
              nestedSubtasks.map((nestedSubtask) => (
                <NestedSubtaskDrawer 
                  key={nestedSubtask.id} 
                  subtask={nestedSubtask} 
                  level={level + 1}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onAddSubtask={onAddSubtask}
                  onLabelsUpdate={handleChildLabelsUpdate}
                  onSubtaskUpdate={onSubtaskUpdate}
                  onCompletionChange={handleSubtaskCompletionChange}
                  fullScreen={fullScreen}
                  readOnly={readOnly}
                />
              ))
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default NestedSubtaskDrawer;
